import type { SunkenLandmarkTemplate } from '../../../../shared/scene/ruinsPropTemplates';
import { townColorHex as hex, type TownPropPalette } from './townPropTemplates';

function shells(points: readonly (readonly [number, number, number])[], p: TownPropPalette): string {
  return points.map(([x, y, r]) => `<g data-part="barnacle">
    <circle cx="${x}" cy="${y}" r="${r}" fill="${hex(p.roofEdge)}"/>
    <circle cx="${x + 1}" cy="${y + 1}" r="${r * .45}" fill="${hex(p.shade)}"/>
  </g>`).join('');
}

function aqueduct(p: TownPropPalette): string {
  const courses = [200, 284, 369, 462].map((y, i) => `
    <path d="M5 ${y}H40 M${i % 2 ? 206 : 203} ${y + 12}H232 M366 ${y - 11}H397"
      stroke="${hex(p.shade)}" stroke-width="3"/>`).join('');
  return `<g data-part="sunken-aqueduct">
    <path d="M0 0H400V560H360V211Q355 121 299 114Q250 124 240 205V337
      L230 353 239 380 243 560H196L203 389 193 365 201 341V190
      Q198 83 121 71Q50 88 47 191V560H0Z" fill="${hex(p.shade)}"/>
    <path d="M0 0H400V18H0Z" fill="${hex(p.primary)}"/>
    <path d="M0 20H399V48L345 43 310 59 284 55 275 42 222 48 204 41 142 48 110 40 64 51 0 45Z" fill="${hex(p.stone)}"/>
    <path d="M6 55L72 53Q32 110 32 194L35 403 29 443 38 528H6Z
      M151 55H207L212 101 224 135 229 294 218 332 213 199Q208 110 151 55Z
      M228 61L286 64Q246 108 234 170L218 110Z
      M325 62L395 53V526H376L375 422 367 378 375 214Q375 117 325 62Z" fill="${hex(p.primary)}"/>
    <path d="M41 192Q42 85 121 63Q204 82 207 191
      M235 206Q247 113 299 106Q362 116 366 213" fill="none" stroke="${hex(p.roofEdge)}" stroke-width="7"/>
    <path d="M43 151l-17 -5 M59 108l-16 -13 M91 77l-8 -17 M148 71l9 -16
      M186 112l14 -12 M202 153l19 -5 M246 155l-14 -8 M272 120l-8 -13
      M319 114l7 -15 M353 157l15 -9" stroke="${hex(p.shade)}" stroke-width="4"/>
    ${courses}
    <path d="M6 395Q20 382 39 399V430Q23 419 6 429Z
      M201 405l17 -8 15 8 2 35 -17 -10 -18 5Z
      M371 398Q387 411 397 393V429L375 441Z" fill="${hex(p.roof)}"/>
    <path d="M9 57l5 52 -3 44 M383 81l-5 51 8 37 M213 214l7 28 -10 29" fill="none" stroke="${hex(p.stone)}" stroke-width="5"/>
    <path d="M197 469l29 -12 11 62 -35 14Z" fill="${hex(p.stone)}"/>
    <path d="M0 535L13 528 27 534 43 526 47 560H0Z
      M196 540L212 530 231 539 243 534V560H196Z
      M358 544L374 528 387 534 400 528V560H358Z" fill="${hex(p.stone)}"/>
    ${shells([[18, 459, 5], [27, 466, 4], [25, 450, 3], [218, 487, 6], [209, 495, 4], [386, 337, 5], [379, 346, 3]], p)}
  </g>`;
}

function brokenArch(p: TownPropPalette): string {
  return `<g data-part="sunken-broken-arch">
    <path d="M9 360L18 229 29 195 36 129 55 92 83 51 112 24 146 10 163 17
      154 43 164 50 145 68 128 63 103 86 79 133 73 196 60 242 57 360Z" fill="${hex(p.shade)}"/>
    <path d="M19 343L26 235 38 198 46 135 62 99 91 62 118 38 145 25 141 44
      128 47 103 69 70 125 65 192 49 237 48 343Z" fill="${hex(p.primary)}"/>
    <path d="M211 112L229 93 238 110 249 105 256 133 253 196 267 246 273 360H219
      L224 294 207 242 211 199 202 178Z" fill="${hex(p.shade)}"/>
    <path d="M221 128L237 120 245 138 243 198 256 249 262 343H231L236 291 221 241 225 197 215 175Z" fill="${hex(p.primary)}"/>
    <path d="M20 279l39 3 M23 239l39 2 M31 195l42 2 M39 152l36 7
      M50 117l31 14 M73 81l25 19 M100 50l22 24 M226 159l26 -4 M219 200l34 6
      M219 243l45 -1 M225 294h43" stroke="${hex(p.shade)}" stroke-width="4"/>
    <path d="M61 228l5 -36 7 -55 24 -41 30 -27" fill="none" stroke="${hex(p.roofEdge)}" stroke-width="3"/>
    <path d="M29 243l-6 51 11 15 -8 30 M246 208l-11 15 8 16 -5 18" fill="none" stroke="${hex(p.roof)}" stroke-width="5"/>
    <path d="M0 347l21 -10 23 6 16 -6 16 23H0Z M208 348l19 -10 20 5 24 -10 9 27H208Z" fill="${hex(p.stone)}"/>
    <path d="M108 360l8 -13 20 -5 17 18Z M151 360l9 -18 10 3 12 15Z" fill="${hex(p.stone)}"/>
    ${shells([[39, 310, 6], [49, 317, 4], [246, 279, 5], [253, 285, 3], [55, 153, 4]], p)}
  </g>`;
}

function reliquary(p: TownPropPalette): string {
  return `<g data-part="sunken-reliquary">
    <g fill="none" stroke="${hex(p.woodEdge)}" stroke-linecap="round" stroke-linejoin="round">
      <path d="M90 109Q107 73 104 26 M102 65L83 43 80 24 M106 76L125 57 130 36
        M104 47L117 31 114 11" stroke-width="5"/>
      <path d="M84 45L69 38 63 24 M125 57L135 56 M102 31L94 21 96 8
        M116 32L128 22 M81 31L86 17 M71 38L70 49 M128 43L121 43" stroke-width="3"/>
    </g>
    <path d="M18 97L37 77 82 73 106 87 118 122 133 132 140 150H0L7 128Z" fill="${hex(p.shade)}"/>
    <path d="M24 100L39 85 82 81 100 92 110 123 125 135H15Z" fill="${hex(p.primary)}"/>
    <path d="M21 108l18 -16 42 -2 12 13 -6 29H28Z" fill="${hex(p.stone)}"/>
    <ellipse cx="61" cy="112" rx="23" ry="20" fill="${hex(p.shade)}"/>
    <ellipse cx="61" cy="111" rx="18" ry="15" fill="${hex(p.roofEdge)}"/>
    <path d="M51 110l10 -9 10 9 -10 10Z M46 113h30" fill="none" stroke="${hex(p.stone)}" stroke-width="3"/>
    <path d="M87 84l-8 11 9 10 -4 9 M27 120l10 9 -5 8" fill="none" stroke="${hex(p.shade)}" stroke-width="3"/>
    <path d="M5 137L32 134 44 140 91 135 118 139 132 135 140 150H0Z" fill="${hex(p.stone)}"/>
    ${shells([[101, 124, 6], [111, 132, 4], [25, 102, 4], [32, 97, 3]], p)}
    <path d="M11 138l3 -15 6 10 3 -17 6 12 -2 11 M118 140l5 -17 3 9 6 -9 1 16" fill="none"
      stroke="${hex(p.roofEdge)}" stroke-width="2"/>
  </g>`;
}

export function buildSunkenLandmarkBody(template: SunkenLandmarkTemplate, palette: TownPropPalette): string {
  switch (template) {
  case 'sunken_aqueduct_front': return aqueduct(palette);
  case 'sunken_broken_arch': return brokenArch(palette);
  case 'sunken_coral_reliquary': return reliquary(palette);
  }
}
